// Ready-made diagrams for the video-session whiteboard.
//
// These are the formulations and worksheets a therapist actually draws in
// session, for the modalities this practice offers (CBT, EMDR, inference-
// based CBT, ACT and IFS). Each one drops a labelled skeleton on the board
// that the therapist then types into — assembling a hot cross bun out of four
// rectangles mid-session is exactly the friction this removes.
//
// A template is plain data: it never touches the network or the database,
// and lives only in the board's display list like anything drawn by hand.

import {
  INKS,
  LABEL_SIZE,
  WHITEBOARD_HEIGHT,
  WHITEBOARD_WIDTH,
  nextId,
  type Align,
  type ArrowEnds,
  type Connector,
  type Item,
  type Pie,
  type PieSlice,
  type Point,
  type Shape,
  type ShapeKind,
  type TextBlock,
} from "./whiteboard-items"

const [INK, RED, BLUE, GREEN] = INKS.map((i) => i.value)

const TINT = {
  red: "#fee2e2",
  blue: "#dbeafe",
  green: "#dcfce7",
  yellow: "#fef9c3",
  purple: "#f3e8ff",
  grey: "#f1f5f9",
} as const

type BoxOptions = {
  shape?: ShapeKind
  fill?: string | null
  color?: string
  size?: number
}

type ArrowOptions = {
  arrow?: ArrowEnds
  bow?: number
  dashed?: boolean
  color?: string
}

type TextOptions = {
  size?: number
  color?: string
  align?: Align
}

// Collects a template's items and hands back ids so connectors can bind to
// shapes. Ids are local to the template; instantiate() rewrites them.
class Builder {
  items: Item[] = []
  private n = 0

  private id(): string {
    this.n += 1
    return `t${this.n}`
  }

  box(x: number, y: number, w: number, h: number, text: string, o: BoxOptions = {}): string {
    const shape: Shape = {
      kind: "shape",
      id: this.id(),
      shape: o.shape ?? "round",
      x,
      y,
      w,
      h,
      color: o.color ?? INK,
      fill: o.fill === undefined ? null : o.fill,
      size: o.size ?? LABEL_SIZE,
      text,
    }
    this.items.push(shape)
    return shape.id
  }

  // Centres a box on a point rather than positioning its corner — the way
  // every radial layout below wants to think.
  at(centre: Point, w: number, h: number, text: string, o: BoxOptions = {}): string {
    return this.box(centre.x - w / 2, centre.y - h / 2, w, h, text, o)
  }

  text(x: number, y: number, text: string, o: TextOptions = {}): string {
    const block: TextBlock = {
      kind: "text",
      id: this.id(),
      color: o.color ?? INK,
      size: o.size ?? LABEL_SIZE,
      x,
      y,
      align: o.align ?? "left",
      text,
    }
    this.items.push(block)
    return block.id
  }

  // `from` and `to` are either a shape id (the connector binds to it and
  // follows it around the board) or a fixed point.
  arrow(from: string | Point, to: string | Point, o: ArrowOptions = {}): string {
    const connector: Connector = {
      kind: "connector",
      id: this.id(),
      color: o.color ?? INK,
      from: this.anchor(from),
      to: this.anchor(to),
      fromId: typeof from === "string" ? from : undefined,
      toId: typeof to === "string" ? to : undefined,
      arrow: o.arrow ?? "end",
      bow: o.bow ?? 0,
      dashed: o.dashed,
    }
    this.items.push(connector)
    return connector.id
  }

  pie(x: number, y: number, r: number, slices: PieSlice[]): string {
    const chart: Pie = { kind: "pie", id: this.id(), x, y, r, color: INK, slices }
    this.items.push(chart)
    return chart.id
  }

  // A bound endpoint's stored point is only a fallback, but keeping it at the
  // shape's centre means unbinding it (by dragging the handle) does not make
  // the connector jump.
  private anchor(ref: string | Point): Point {
    if (typeof ref !== "string") return ref
    const shape = this.items.find((item) => item.id === ref)
    if (shape?.kind === "shape") return { x: shape.x + shape.w / 2, y: shape.y + shape.h / 2 }
    return { x: 0, y: 0 }
  }
}

export type Template = {
  key: string
  name: string
  group: string
  build: (b: Builder) => void
}

function radial(cx: number, cy: number, rx: number, ry: number, degrees: number): Point {
  const radians = (degrees * Math.PI) / 180
  return { x: cx + Math.cos(radians) * rx, y: cy + Math.sin(radians) * ry }
}

const CENTRE_X = WHITEBOARD_WIDTH / 2
const CENTRE_Y = WHITEBOARD_HEIGHT / 2

export const TEMPLATES: Template[] = [
  // ---- CBT formulations -----------------------------------------------------
  {
    key: "hot-cross-bun",
    name: "Hot cross bun (five areas)",
    group: "CBT formulation",
    build: (b) => {
      const situation = b.box(560, 40, 480, 90, "Situation / trigger", { fill: TINT.yellow })
      const thoughts = b.box(600, 210, 400, 140, "Thoughts", { fill: TINT.blue })
      const feelings = b.box(170, 470, 380, 140, "Feelings", { fill: TINT.red })
      const physical = b.box(1050, 470, 380, 140, "Physical sensations", { fill: TINT.purple })
      const behaviour = b.box(600, 760, 400, 140, "Behaviour", { fill: TINT.green })
      b.arrow(situation, thoughts)
      for (const [from, to] of [
        [thoughts, feelings],
        [thoughts, physical],
        [feelings, behaviour],
        [physical, behaviour],
        [thoughts, behaviour],
        [feelings, physical],
      ]) {
        b.arrow(from, to, { arrow: "both" })
      }
    },
  },
  {
    key: "vicious-flower",
    name: "Vicious flower",
    group: "CBT formulation",
    build: (b) => {
      const centre = b.at({ x: CENTRE_X, y: CENTRE_Y }, 420, 240, "Central fear or belief", {
        shape: "ellipse",
        fill: TINT.red,
      })
      const petals = [
        "Checking",
        "Reassurance seeking",
        "Avoidance",
        "Rumination and mental review",
        "Self-monitoring",
        "Safety behaviours",
      ]
      petals.forEach((label, i) => {
        const petal = b.at(radial(CENTRE_X, CENTRE_Y, 520, 340, -90 + i * 60), 300, 130, label, {
          shape: "ellipse",
          fill: TINT.blue,
          size: 26,
        })
        b.arrow(centre, petal, { arrow: "both" })
      })
    },
  },
  {
    key: "ocd-cycle",
    name: "OCD maintenance cycle",
    group: "CBT formulation",
    build: (b) => {
      const labels = [
        "Intrusive thought",
        "Appraisal:\n“this means something about me”",
        "Anxiety and distress",
        "Compulsion or neutralising",
        "Short-term relief",
      ]
      const ring = labels.map((label, i) =>
        b.at(radial(CENTRE_X, CENTRE_Y, 520, 330, -90 + i * 72), 360, 130, label, {
          fill: i === 4 ? TINT.green : TINT.blue,
          size: 26,
        }),
      )
      ring.forEach((from, i) => b.arrow(from, ring[(i + 1) % ring.length], { bow: -45 }))
    },
  },
  {
    key: "panic-cycle",
    name: "Panic cycle (Clark)",
    group: "CBT formulation",
    build: (b) => {
      const cx = 880
      const cy = 520
      const trigger = b.box(80, 60, 340, 110, "Trigger\ninternal or external", { fill: TINT.yellow, size: 26 })
      const ring = [
        ["Perceived threat", TINT.blue],
        ["Apprehension", TINT.blue],
        ["Body sensations", TINT.purple],
        ["Catastrophic misinterpretation", TINT.red],
      ].map(([label, fill], i) => b.at(radial(cx, cy, 420, 300, -90 + i * 90), 380, 130, label, { fill, size: 26 }))
      b.arrow(trigger, ring[0])
      ring.forEach((from, i) => b.arrow(from, ring[(i + 1) % ring.length], { bow: -45 }))
    },
  },
  {
    key: "longitudinal",
    name: "Longitudinal formulation (Beck)",
    group: "CBT formulation",
    build: (b) => {
      const rows = [
        "Early experiences",
        "Core beliefs",
        "Rules and assumptions",
        "Critical incident",
        "Negative automatic thoughts",
        "Maintenance: behaviour, mood, body",
      ]
      let previous: string | null = null
      rows.forEach((label, i) => {
        const box = b.box(450, 30 + i * 160, 700, 110, label, { fill: i === 3 ? TINT.yellow : TINT.blue })
        if (previous) b.arrow(previous, box)
        previous = box
      })
    },
  },
  {
    key: "cognitive-triangle",
    name: "Cognitive triangle",
    group: "CBT formulation",
    build: (b) => {
      const thoughts = b.box(620, 90, 360, 130, "Thoughts", { fill: TINT.blue })
      const feelings = b.box(200, 680, 360, 130, "Feelings", { fill: TINT.red })
      const behaviour = b.box(1040, 680, 360, 130, "Behaviour", { fill: TINT.green })
      b.arrow(thoughts, feelings, { arrow: "both" })
      b.arrow(feelings, behaviour, { arrow: "both" })
      b.arrow(behaviour, thoughts, { arrow: "both" })
    },
  },
  {
    key: "theory-a-b",
    name: "Theory A / Theory B",
    group: "CBT formulation",
    build: (b) => {
      b.box(90, 60, 680, 110, "Theory A\nThe danger is real", { fill: TINT.red, size: 28 })
      b.box(830, 60, 680, 110, "Theory B\nI am worried the danger is real", { fill: TINT.green, size: 28 })
      b.box(90, 200, 680, 320, "What is the evidence?")
      b.box(830, 200, 680, 320, "What is the evidence?")
      b.box(90, 550, 680, 300, "If this is true, what do I need to do?")
      b.box(830, 550, 680, 300, "If this is true, what do I need to do?")
    },
  },
  {
    key: "abc",
    name: "ABC functional analysis",
    group: "CBT formulation",
    build: (b) => {
      const headers = [
        "A — Antecedent\nWhat came before?",
        "B — Behaviour\nWhat did I do?",
        "C — Consequence\nWhat happened after?",
      ]
      headers.forEach((label, i) => {
        const x = 80 + i * 490
        b.box(x, 60, 460, 110, label, { fill: TINT.blue, size: 26 })
        b.box(x, 190, 460, 730, "")
      })
    },
  },

  // ---- Worksheets -----------------------------------------------------------
  {
    key: "thought-record",
    name: "Thought record",
    group: "Worksheets",
    build: (b) => {
      const headers = [
        "Situation",
        "Thought\n(belief %)",
        "Emotion\n(0–100)",
        "Evidence for",
        "Evidence against",
        "Balanced thought\n(belief %)",
      ]
      headers.forEach((label, column) => {
        const x = 40 + column * 253
        b.box(x, 50, 253, 110, label, { shape: "rect", fill: TINT.blue, size: 23 })
        for (let row = 0; row < 3; row++) b.box(x, 160 + row * 250, 253, 250, "", { shape: "rect" })
      })
    },
  },
  {
    key: "exposure-ladder",
    name: "Exposure ladder",
    group: "Worksheets",
    build: (b) => {
      b.text(300, 8, "Hardest at the top", { size: 26, color: BLUE })
      b.text(1240, 8, "SUDs", { size: 26, color: BLUE })
      for (let i = 0; i < 7; i++) {
        const y = 70 + i * 115
        b.box(300, y, 900, 105, "", { shape: "rect" })
        b.box(1240, y, 160, 105, "", { shape: "rect", fill: TINT.yellow })
      }
      b.text(300, 890, "Start low, and stay with it until the anxiety comes down on its own.", { size: 26, color: BLUE })
    },
  },

  // ---- Charts ---------------------------------------------------------------
  {
    key: "responsibility-pie",
    name: "Responsibility pie",
    group: "Charts",
    build: (b) => {
      b.text(60, 50, "Who or what is responsible?\nDrag the edges to share it out.", { size: 28, color: BLUE })
      b.pie(700, 540, 300, [
        { text: "Me", value: 25, color: TINT.red },
        { text: "Chance or bad luck", value: 20, color: TINT.blue },
        { text: "Other people", value: 20, color: TINT.green },
        { text: "How I was brought up", value: 15, color: TINT.yellow },
        { text: "Outside anyone's control", value: 20, color: TINT.purple },
      ])
    },
  },
  {
    key: "anxiety-curve",
    name: "Anxiety curve (habituation)",
    group: "Charts",
    build: (b) => {
      b.arrow({ x: 200, y: 850 }, { x: 200, y: 110 })
      b.arrow({ x: 200, y: 850 }, { x: 1470, y: 850 })
      b.arrow({ x: 200, y: 480 }, { x: 1440, y: 480 }, { arrow: "none", dashed: true, color: BLUE })
      b.text(150, 55, "Anxiety 0–100", { size: 26, color: BLUE })
      b.text(1390, 880, "Time", { size: 26, color: BLUE })
      b.text(120, 100, "100", { size: 24, color: BLUE })
      b.text(140, 460, "50", { size: 24, color: BLUE })
      b.text(160, 820, "0", { size: 24, color: BLUE })
      b.text(300, 910, "Draw what you expect to happen — then what actually happened.", { size: 26, color: BLUE })
    },
  },
  {
    key: "wheel-of-life",
    name: "Wheel of life",
    group: "Charts",
    build: (b) => {
      for (const r of [120, 240, 360]) {
        b.at({ x: CENTRE_X, y: CENTRE_Y }, r * 2, r * 2, "", { shape: "ellipse", color: BLUE })
      }
      const spokes = ["Health", "Work", "Money", "Family", "Friends", "Fun", "Growth", "Home"]
      spokes.forEach((label, i) => {
        const degrees = -90 + i * 45
        b.arrow({ x: CENTRE_X, y: CENTRE_Y }, radial(CENTRE_X, CENTRE_Y, 360, 360, degrees), { arrow: "none", color: BLUE })
        const at = radial(CENTRE_X, CENTRE_Y, 430, 420, degrees)
        b.text(at.x, at.y - 20, label, { align: "center", size: 28 })
      })
      b.text(60, 40, "Mark each spoke 0–10, then join the marks up.", { size: 26, color: BLUE })
    },
  },

  // ---- ACT ------------------------------------------------------------------
  {
    key: "choice-point",
    name: "Choice point",
    group: "ACT",
    build: (b) => {
      const situation = b.box(610, 830, 380, 110, "Situation", { fill: TINT.yellow })
      const away = b.box(150, 110, 420, 150, "Away moves\nwhat I do when I'm hooked", { fill: TINT.red, size: 26 })
      const towards = b.box(1030, 110, 420, 150, "Towards moves\nwhat matters to me", { fill: TINT.green, size: 26 })
      b.arrow(situation, away)
      b.arrow(situation, towards)
      b.text(300, 400, "What hooks me?", { size: 28, align: "center", color: RED })
      b.text(1240, 400, "What helps me?", { size: 28, align: "center", color: GREEN })
    },
  },
  {
    key: "values-bullseye",
    name: "Values bullseye",
    group: "ACT",
    build: (b) => {
      for (const r of [90, 180, 270, 360]) {
        b.at({ x: CENTRE_X, y: CENTRE_Y }, r * 2, r * 2, "", { shape: "ellipse", color: BLUE })
      }
      b.arrow({ x: CENTRE_X, y: 120 }, { x: CENTRE_X, y: 880 }, { arrow: "none", color: BLUE })
      b.arrow({ x: 420, y: CENTRE_Y }, { x: 1180, y: CENTRE_Y }, { arrow: "none", color: BLUE })
      b.text(330, 170, "Work / education", { align: "center", size: 28 })
      b.text(1270, 170, "Leisure", { align: "center", size: 28 })
      b.text(330, 790, "Relationships", { align: "center", size: 28 })
      b.text(1270, 790, "Personal growth\nand health", { align: "center", size: 28 })
      b.text(60, 50, "Mark an X in each quadrant: how close are you to living that value?", { size: 26, color: BLUE })
    },
  },
  {
    key: "hexaflex",
    name: "Hexaflex",
    group: "ACT",
    build: (b) => {
      const centre = b.at({ x: CENTRE_X, y: CENTRE_Y }, 340, 200, "Psychological flexibility", {
        shape: "ellipse",
        fill: TINT.green,
        size: 26,
      })
      const nodes = [
        "Present moment",
        "Values",
        "Committed action",
        "Self-as-context",
        "Defusion",
        "Acceptance",
      ]
      nodes.forEach((label, i) => {
        const node = b.at(radial(CENTRE_X, CENTRE_Y, 520, 340, -90 + i * 60), 300, 130, label, {
          shape: "ellipse",
          fill: TINT.blue,
          size: 26,
        })
        b.arrow(centre, node, { arrow: "none" })
      })
    },
  },

  // ---- IFS and EMDR ---------------------------------------------------------
  {
    key: "parts-map",
    name: "Parts map (IFS)",
    group: "IFS and EMDR",
    build: (b) => {
      const self = b.at({ x: CENTRE_X, y: CENTRE_Y }, 300, 190, "Self", { shape: "ellipse", fill: TINT.green })
      const parts: [string, string, boolean][] = [
        ["Manager", "200,150", false],
        ["Manager", "1100,150", false],
        ["Firefighter", "1180,450", false],
        ["Exile", "1100,760", true],
        ["Exile", "200,760", true],
        ["Protector", "140,450", false],
      ]
      for (const [label, position, exile] of parts) {
        const [x, y] = position.split(",").map(Number)
        const part = b.box(x, y, 300, 130, label, { shape: "ellipse", fill: exile ? TINT.red : TINT.blue })
        b.arrow(self, part, { arrow: "none", dashed: exile })
      }
      b.text(60, 40, "Which part is here right now? How does Self feel towards it?", { size: 26, color: BLUE })
    },
  },
  {
    key: "emdr-timeline",
    name: "Target sequence timeline (EMDR)",
    group: "IFS and EMDR",
    build: (b) => {
      b.text(60, 60, "Touchstone memory → present triggers → future template", { size: 28, color: BLUE })
      b.arrow({ x: 120, y: 560 }, { x: 1500, y: 560 })
      for (let i = 0; i < 5; i++) {
        const x = 140 + i * 280
        const box = b.box(x, 340, 250, 140, "", { fill: TINT.blue })
        b.arrow(box, { x: x + 125, y: 560 }, { arrow: "none" })
      }
      b.text(220, 600, "Past", { align: "center", size: 28 })
      b.text(810, 600, "Present", { align: "center", size: 28 })
      b.text(1390, 600, "Future", { align: "center", size: 28 })
    },
  },

  // ---- General --------------------------------------------------------------
  {
    key: "window-of-tolerance",
    name: "Window of tolerance",
    group: "General",
    build: (b) => {
      b.text(180, 55, "Where were you today? Draw the line.", { size: 26, color: BLUE })
      b.box(180, 120, 1240, 200, "Hyperarousal — fight or flight, panic, anger", { fill: TINT.red, size: 28 })
      b.box(180, 340, 1240, 280, "Window of tolerance — able to think and feel at the same time", {
        fill: TINT.green,
        size: 28,
      })
      b.box(180, 640, 1240, 200, "Hypoarousal — shut down, numb, disconnected", { fill: TINT.blue, size: 28 })
      b.text(180, 870, "Widening the window is the work.", { size: 26, color: BLUE })
    },
  },
  {
    key: "stress-bucket",
    name: "Stress bucket",
    group: "General",
    build: (b) => {
      b.text(60, 40, "What is going in? What lets some out?", { size: 26, color: BLUE })
      const bucket = b.box(560, 320, 480, 520, "What I'm carrying", { shape: "rect", fill: TINT.blue })
      const sources = [
        ["Work", 190],
        ["Family", 520],
        ["Health", 900],
        ["Money", 1230],
      ] as const
      for (const [label, x] of sources) {
        b.text(x, 130, label, { align: "center", size: 28 })
        b.arrow({ x, y: 185 }, bucket)
      }
      const tap = b.box(1040, 745, 150, 70, "", { shape: "rect", fill: TINT.green })
      b.arrow(tap, { x: 1440, y: 860 })
      b.text(1250, 890, "Coping — what lets some out?", { align: "center", size: 26, color: GREEN })
    },
  },
  {
    key: "drama-triangle",
    name: "Drama triangle",
    group: "General",
    build: (b) => {
      const persecutor = b.box(170, 120, 380, 130, "Persecutor", { fill: TINT.red })
      const rescuer = b.box(1050, 120, 380, 130, "Rescuer", { fill: TINT.blue })
      const victim = b.box(610, 720, 380, 130, "Victim", { fill: TINT.yellow })
      b.arrow(persecutor, rescuer, { arrow: "both" })
      b.arrow(rescuer, victim, { arrow: "both" })
      b.arrow(victim, persecutor, { arrow: "both" })
      b.text(CENTRE_X, 20, "Which corner am I in?\nWhat would step me out of it?", { align: "center", size: 28, color: BLUE })
    },
  },
  {
    key: "genogram",
    name: "Genogram",
    group: "General",
    build: (b) => {
      // Standard genogram notation: square for male, circle for female, a
      // line between partners, descent lines down to the children.
      const father = b.box(500, 140, 160, 160, "", { shape: "rect" })
      const mother = b.box(940, 140, 160, 160, "", { shape: "ellipse" })
      b.arrow(father, mother, { arrow: "none" })
      b.arrow({ x: CENTRE_X, y: 220 }, { x: CENTRE_X, y: 430 }, { arrow: "none" })
      b.arrow({ x: 560, y: 430 }, { x: 1040, y: 430 }, { arrow: "none" })
      b.arrow({ x: 560, y: 430 }, { x: 560, y: 560 }, { arrow: "none" })
      b.arrow({ x: 1040, y: 430 }, { x: 1040, y: 560 }, { arrow: "none" })
      b.box(480, 560, 160, 160, "", { shape: "rect" })
      b.box(960, 560, 160, 160, "", { shape: "ellipse" })
      b.text(80, 800, "Square = male   Circle = female\nDouble-click a symbol to add a name or age.", {
        size: 26,
        color: BLUE,
      })
    },
  },
]

// Template ids are local to the template, so every insertion remaps them to
// fresh board ids — including the ids connectors bind to — and deep-copies
// the mutable parts, so inserting the same template twice gives two
// independent diagrams.
export function instantiate(template: Template): Item[] {
  const builder = new Builder()
  template.build(builder)
  const remap = new Map(builder.items.map((item) => [item.id, nextId()]))
  return builder.items.map((item) => {
    const next = { ...item, id: remap.get(item.id)! }
    if (next.kind === "connector") {
      next.from = { ...next.from }
      next.to = { ...next.to }
      if (next.fromId) next.fromId = remap.get(next.fromId)
      if (next.toId) next.toId = remap.get(next.toId)
    }
    if (next.kind === "stroke") next.points = next.points.map((p) => ({ ...p }))
    if (next.kind === "pie") next.slices = next.slices.map((slice) => ({ ...slice }))
    return next
  })
}

export const TEMPLATE_GROUPS: { group: string; templates: Template[] }[] = TEMPLATES.reduce(
  (groups, template) => {
    const existing = groups.find((g) => g.group === template.group)
    if (existing) existing.templates.push(template)
    else groups.push({ group: template.group, templates: [template] })
    return groups
  },
  [] as { group: string; templates: Template[] }[],
)
